'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

type Snapshot = {
  playing: boolean
  duration: number
  ready: boolean
  error: boolean
  direction: 'forward' | 'reverse'
  element: HTMLVideoElement | null
}
type Controls = { play: () => void; pause: () => void; seek: (time: number) => void; time: () => number }

/** Owns one native video; reverse playback seeks only after the previous frame decoded. */
export function usePreviewVideo({
  video,
  enabled,
  bounce,
  speed,
  onEnded,
}: {
  video?: HTMLVideoElement | null
  enabled: boolean
  bounce: boolean
  speed: number
  onEnded: () => void
}) {
  const [state, setState] = useState<Snapshot>({
    playing: false,
    duration: 0,
    ready: false,
    error: false,
    direction: 'forward',
    element: null,
  })
  const options = useRef({ bounce, speed, onEnded })
  options.current = { bounce, speed, onEnded }
  const controls = useRef<Controls | null>(null)

  useEffect(() => {
    if (!enabled || !video) return
    let reverse = false
    let running = !video.paused && !video.ended
    let cursor = video.currentTime
    let frame = 0
    let previous = 0
    let alive = true
    const sync = () => {
      if (!alive) return
      const next: Snapshot = {
        playing: reverse ? running : !video.paused && !video.ended,
        duration: Number.isFinite(video.duration) ? video.duration : 0,
        ready: video.readyState >= 2,
        error: !!video.error,
        direction: reverse ? 'reverse' : 'forward',
        element: video,
      }
      setState((previous) =>
        previous.playing === next.playing &&
        previous.duration === next.duration &&
        previous.ready === next.ready &&
        previous.error === next.error &&
        previous.direction === next.direction &&
        previous.element === next.element
          ? previous
          : next
      )
    }
    const forward = () => {
      reverse = false
      cancelAnimationFrame(frame)
      running = true
      void video.play().catch(() => {
        running = false
        sync()
      })
      sync()
    }
    const tick = (now: number) => {
      if (!alive || !reverse || !running) return
      cursor = Math.max(0, cursor - Math.min(0.1, (now - previous) / 1000) * options.current.speed)
      previous = now
      if (!options.current.bounce) {
        forward()
        return
      }
      if (!video.seeking) {
        video.currentTime = cursor
        if (cursor <= 0) {
          forward()
          return
        }
      }
      frame = requestAnimationFrame(tick)
    }
    const startReverse = () => {
      cancelAnimationFrame(frame)
      previous = performance.now()
      frame = requestAnimationFrame(tick)
      sync()
    }
    const onPlay = () => {
      reverse = false
      running = true
      cancelAnimationFrame(frame)
      sync()
    }
    const onPause = () => {
      if (!reverse && !video.ended) running = false
      sync()
    }
    const onEnd = () => {
      if (running && options.current.bounce && video.duration > 0) {
        reverse = true
        cursor = video.duration
        startReverse()
      } else {
        const advance = running
        running = false
        sync()
        if (advance) options.current.onEnded()
      }
    }
    const api: Controls = {
      play: () => {
        if (reverse && options.current.bounce) {
          running = true
          startReverse()
        } else forward()
      },
      pause: () => {
        running = false
        cancelAnimationFrame(frame)
        video.pause()
        sync()
      },
      seek: (time) => {
        cursor = Math.min(Number.isFinite(video.duration) ? video.duration : 0, Math.max(0, time))
        video.currentTime = cursor
      },
      time: () => (reverse ? cursor : video.currentTime),
    }
    controls.current = api
    const events = ['durationchange', 'loadedmetadata', 'loadeddata', 'error', 'seeked']
    events.forEach((event) => video.addEventListener(event, sync))
    video.addEventListener('play', onPlay)
    video.addEventListener('pause', onPause)
    video.addEventListener('ended', onEnd)
    sync()
    return () => {
      alive = false
      cancelAnimationFrame(frame)
      events.forEach((event) => video.removeEventListener(event, sync))
      video.removeEventListener('play', onPlay)
      video.removeEventListener('pause', onPause)
      video.removeEventListener('ended', onEnd)
      if (controls.current === api) controls.current = null
    }
  }, [enabled, video])

  // Leaving bounce during its reverse leg continues forwards from the current position.
  useEffect(() => {
    if (!bounce && state.direction === 'reverse') {
      if (state.playing) controls.current?.play()
    }
  }, [bounce, state.direction, state.playing])

  const play = useCallback(() => controls.current?.play(), [])
  const pause = useCallback(() => controls.current?.pause(), [])
  const seek = useCallback((time: number) => controls.current?.seek(time), [])
  const time = useCallback(() => controls.current?.time() ?? 0, [])
  return { ...state, play, pause, seek, time }
}
